import React, {useCallback, useEffect, useMemo, useState} from "react";
import {difference, isEmpty, union, uniq} from "lodash";
import classNames from "classnames";
import {Button, Checkbox, ConfigProvider, message, Select, Switch, Table} from "antd";
import {filterProducts} from "./utils";
import {MarketplaceSearchInput} from "../../marketplace/MarketplaceSearchInput";
import {HttpClient} from "../../http/HttpClient";
import {useRequest} from "../../utils/hooks";
import ProductsFiltersModal from "../../marketplace/ProductsFilterModal";
import {MarketplaceFilterSearchOptions} from "../../marketplace/MarketplaceFilterSearchOptions";
import {NOOP} from "../../utils/NOOP";
import {ConfirmationRequiredWrapper} from "../../components";

export const DEFAULT_PAGE = 60;
const {Option} = Select;

export const AdminProductsBase = ({
    currentCategory,
    setCurrentCategory,
    setCurrentTablePage,
    currentSubCategory,
    setCurrentSubCategory,
    currentSubCategoryItems,
    setCurrentSubCategoryItems,
    extraFilters,
    setExtraFilters,
    loadingTable,
    tableProps,
    rowSelection,
    topBarButtons,
    filterValues,
    lockedFilters = [],
    onLockedFiltersChange = NOOP,
    columns = []
}) => {
    const [allProducts, setAllProducts] = useState([]);
    const [productFiltersModalVisible, setProductFiltersModalVisible] = useState(false);
    const [subCategoriesList, setSubCategoriesList] = useState([]);
    const [searchText, setSearchText] = useState("");
    const [searchedTag, setSearchedTag] = useState("");
    const [viewSelectedProducts, setViewSelectedProducts] = useState(false);
    const [categories, loadingCategories, __, fetchCategories] = useRequest("/api/v2/categories", "post", {});
    const [tagProductIds, loadingTagProductsIds] = useRequest(
        `/api/searchTags/productIdsByTagIds`,
        "post",
        [!!searchedTag?.tagId],
        [],
        !!searchedTag?.tagId
    );
    const [allSearchTags = [], loadSearchTags, _, fetchSearchTags] = useRequest(
        "/admin/api/searchTags",
        "get",
        null,
        [],
        true,
        null,
        []
    );

    const fetchAllProducts = useCallback(async (skipCache = false) => {
        const allProducts = await HttpClient.safeGet(`/api/products/getProducts?skipCache=${skipCache}`);

        if (!allProducts.error) {
            setAllProducts(allProducts.products);
        }
    }, []);

    useEffect(() => {
        fetchAllProducts();
        setCurrentCategory("*");
    }, []);

    const onRefresh = useCallback(async () => {
        await fetchAllProducts(true);
        HttpClient.safeGet("/admin/api/search/generateSearchOptions");
    }, []);

    useEffect(() => {
        const {searchText = "", searchedTag = "", ...rest} = filterValues ?? {};
        setSearchText(searchText);
        setSearchedTag(searchedTag);
        setExtraFilters(rest);
    }, [filterValues]);

    const filteredProductSearch = useMemo(() => {
        const productsToFilter = viewSelectedProducts
            ? currentSubCategoryItems.filter(({productId}) => rowSelection.selectedRowKeys.includes(productId))
            : currentSubCategoryItems;
        return filterProducts(
            productsToFilter,
            searchText,
            extraFilters,
            searchedTag?.name === searchText ? tagProductIds : [],
            viewSelectedProducts ? [] : extraFilters?.extraSearchTexts ?? []
        );
    }, [
        currentCategory,
        currentSubCategoryItems,
        searchText,
        extraFilters,
        viewSelectedProducts,
        rowSelection?.selectedRowKeys,
        searchedTag,
        tagProductIds
    ]);

    const onTagSelected = useCallback(
        tag => {
            const newTags = union(extraFilters?.searchTags ?? [], tag.tagIds);
            setExtraFilters(extraFilters => ({...extraFilters, searchTags: newTags}));
        },
        [extraFilters]
    );

    const addTagByName = useCallback(
        name => {
            const tag = allSearchTags.find(tag => tag.name === name) ?? null;
            if (!tag) return;
            onTagSelected({tagIds: [tag.tagId]});
        },
        [searchedTag, onTagSelected]
    );

    const shouldLoadTable = useMemo(
        () => !!(!allProducts.length || loadingCategories || loadingTable || loadingTagProductsIds),
        [allProducts, loadingCategories, loadingTable, loadingTagProductsIds]
    );
    const extraValuesDefaultValues = useMemo(() => {
        if (!filterValues) {
            return null;
        }

        const {searchText, searchedTag, ...rest} = filterValues;
        return rest;
    }, [filterValues]);

    const getProductsBySubCategories = useCallback(async () => {
        const subCategoryId = subCategoriesList.find(subCategory => subCategory.name === currentSubCategory)?.id;
        return await HttpClient.safeGet(`/api/products/getProductIdsBySubCategory/${subCategoryId}`);
    }, [currentSubCategory]);

    const categoryProducts = useMemo(
        () => allProducts.filter(({services}) => services.includes(currentCategory)),
        [allProducts, currentCategory]
    );

    const fetchProductsBySubCategories = useCallback(async () => {
        if (currentCategory === "*") {
            setCurrentSubCategoryItems(allProducts);
        } else if (currentSubCategory === "*") {
            setCurrentSubCategoryItems(categoryProducts);
        } else if (currentSubCategory) {
            const sortedProducts = await getProductsBySubCategories();
            const tableItems = sortedProducts.products
                .map(productId => categoryProducts.find(product => product.productId === productId))
                .filter(product => product);
            setCurrentSubCategoryItems(tableItems);
        }
    }, [currentCategory, currentSubCategory, categoryProducts]);

    useEffect(() => {
        fetchProductsBySubCategories();
    }, [allProducts, currentCategory, currentSubCategory]);

    useEffect(() => {
        if (currentCategory === "*") {
            setCurrentSubCategoryItems(allProducts);
            setCurrentSubCategory(null);
        } else {
            const subCategories = categories?.find(category => category.serviceId === currentCategory)?.subCategories;
            setSubCategoriesList(subCategories);
        }
    }, [currentCategory]);

    const allFilterValues = useMemo(
        () => ({
            ...extraFilters,
            searchText,
            searchedTag
        }),
        [extraFilters, searchText, searchedTag]
    );

    const headerCheckbox = useMemo(
        () =>
            rowSelection ? (
                <Checkbox
                    //TODO: check if can be simplified
                    checked={
                        rowSelection.selectedRowKeys.length &&
                        filteredProductSearch.length &&
                        rowSelection.selectedRowKeys.length >= filteredProductSearch.length &&
                        difference(
                            filteredProductSearch.map(r => r.productId),
                            rowSelection.selectedRowKeys
                        ).length === 0
                    }
                    indeterminate={
                        rowSelection.selectedRowKeys.length &&
                        filteredProductSearch.length &&
                        difference(
                            filteredProductSearch.map(r => r.productId),
                            rowSelection.selectedRowKeys
                        ).length !== 0 &&
                        union(
                            filteredProductSearch.map(r => r.productId),
                            rowSelection.selectedRowKeys
                        ).length > 0
                    }
                    onChange={() => {
                        const currentProductsIds = filteredProductSearch.map(r => r.productId);
                        if (difference(currentProductsIds, rowSelection.selectedRowKeys).length === 0) {
                            rowSelection.onChange(currentProductsIds, false);
                            return;
                        }

                        rowSelection.onChange(currentProductsIds, true);
                    }}
                />
            ) : null,
        [rowSelection, filteredProductSearch]
    );

    const onTextSearch = useCallback(() => {
        setExtraFilters(currentExtraFilters => {
            const {extraSearchTexts = [], ...restExtraFilters} = currentExtraFilters;
            const newExtraSearchTexts = uniq([searchText, ...extraSearchTexts]);

            return {extraSearchTexts: newExtraSearchTexts, ...restExtraFilters};
        });
        setSearchText("");
    }, [extraFilters, searchText]);

    return (
        <ConfigProvider direction="rtl">
            <div className="admin-product-content">
                <ProductsFiltersModal
                    filters={extraValuesDefaultValues}
                    onClose={() => setProductFiltersModalVisible(false)}
                    onCancel={() => {
                        setExtraFilters({});
                        setProductFiltersModalVisible(false);
                    }}
                    onSave={() => {
                        setProductFiltersModalVisible(false);
                    }}
                    visible={productFiltersModalVisible}
                    onFiltersChange={setExtraFilters}
                    foundProductsCount={filteredProductSearch?.length}
                />
                <div className="admin-product-top-bar">
                    <div className="admin-product-search-container">
                        <div id="category-select">
                            <Select
                                showSearch
                                defaultValue={"*"}
                                defaultActiveFirstOption={true}
                                value={currentCategory}
                                disabled={!categories}
                                onSelect={setCurrentCategory}
                                placeholder="קטגוריה"
                                className="admin-product-search-select"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                optionFilterProp="children"
                                listHeight={600}>
                                <Option value="*">כל הקטגוריות</Option>
                                {categories?.map(category => (
                                    <Option key={category.serviceId} value={category.serviceId}>
                                        {category.displayName ?? category.categoryName}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <div id="sub-category-select">
                            <Select
                                showSearch
                                value={currentSubCategory}
                                onSelect={setCurrentSubCategory}
                                placeholder="תת קטגוריה"
                                disabled={!currentCategory || currentCategory === "*" || !subCategoriesList}
                                className="admin-product-search-select"
                                filterOption={(input, option) =>
                                    option.children.toLowerCase().includes(input.toLowerCase())
                                }
                                optionFilterProp="children">
                                <Option value="*">כל תתגי הקטגוריות</Option>
                                {subCategoriesList?.map(subCategory => (
                                    <Option key={subCategory.id} value={subCategory.name}>
                                        {subCategory.name}
                                    </Option>
                                ))}
                            </Select>
                        </div>
                        <div id="filters-button">
                            <Button
                                className={classNames("admin-product-search-refresh-button", {
                                    isActive: !isEmpty(extraFilters)
                                })}
                                onClick={() => {
                                    setProductFiltersModalVisible(true);
                                }}>
                                מסננים
                            </Button>
                        </div>
                        <div id="refresh-button">
                            <ConfirmationRequiredWrapper
                                onYes={onRefresh}
                                title="שימו לב!"
                                subTitle="אין צורך לרענן מוצרים יותר במערכת, השימוש בכפתור תחת אחריותכם בלבד ו/או באישור מתכנת">
                                <Button className="admin-product-search-refresh-button">רענן מוצרים</Button>
                            </ConfirmationRequiredWrapper>
                        </div>
                        <div id="show-selection-button">
                            {rowSelection ? (
                                <Button
                                    className={classNames("admin-product-search-refresh-button", {
                                        isActive: viewSelectedProducts
                                    })}
                                    onClick={() => {
                                        if (!currentCategory) {
                                            setCurrentCategory("*");
                                        }
                                        setViewSelectedProducts(prev => !prev);
                                    }}>
                                    הצג מוצרים מסומנים
                                </Button>
                            ) : null}
                        </div>
                        {topBarButtons}
                    </div>
                    <div id="search-input" className="admin-product-search-container">
                        <MarketplaceSearchInput
                            className="admin-product-search-input"
                            history={history}
                            searchInput={searchText}
                            setSearchInput={setSearchText}
                            placeholder="חיפוש חופשי"
                            debouncedSearchText={searchText}
                            setDebouncedSearchText={setSearchText}
                            showFilters={false}
                            showSearchButton={false}
                            showFiltersButton={false}
                            onItemClick={({link, text}) => {
                                setSearchText(text);
                            }}
                            onEnter={e => {
                                onTextSearch();
                                addTagByName(e.target.value);
                            }}
                            tagClicked={onTagSelected}
                        />
                    </div>
                </div>
                <MarketplaceFilterSearchOptions
                    filterValues={allFilterValues}
                    setFilterValues={setExtraFilters}
                    onLockedFiltersChange={onLockedFiltersChange}
                    lockedFilters={lockedFilters}
                    allSearchTags={allSearchTags}
                    removeSearchInput={() => {
                        setSearchText("");
                        setSearchedTag("");
                    }}
                    removeExtraSearchInput={searchText => {
                        setExtraFilters(currentExtraFilters => {
                            const {extraSearchTexts, ...restExtraFilters} = currentExtraFilters;

                            const newExtraSearchTexts = extraSearchTexts.filter(text => text !== searchText);

                            return {extraSearchTexts: newExtraSearchTexts, ...restExtraFilters};
                        });
                    }}
                />

                <Table
                    rowKey="productId"
                    className="admin-product-table-container"
                    scroll={{x: 800}}
                    pagination={{
                        position: ["bottomCenter"],
                        showSizeChanger: false,
                        defaultPageSize: DEFAULT_PAGE,
                        showTotal: total => <span>סה״כ {total}</span>,
                        onChange: (page, pageSize) => setCurrentTablePage(page - 1)
                    }}
                    loading={shouldLoadTable}
                    dataSource={filteredProductSearch || []}
                    columns={columns}
                    {...(rowSelection
                        ? {
                              rowSelection: {
                                  ...rowSelection,
                                  columnTitle: headerCheckbox,
                                  onSelect: ({productId}, selected) => rowSelection.onChange([productId], selected)
                              }
                          }
                        : {})}
                    {...tableProps}
                />
            </div>
        </ConfigProvider>
    );
};
